export {StateEventManager as default};

/**
* State Event Manager
*
* Allows set of callbacks when state changes
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class StateEventManager {
  /**
   * Defines loading state
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {Object}
   */
  events = {};

  /**
   * Stores all callback functions
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {Object}
   */
  callbacks = {};

  /**
   * Gets the state of an event
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String|Array} id
   *
   * @return {String|null}
   */
  get(id) {
    return this.events[this.getId(id)] || null;
  }

  /**
   * Sets a custom event event state
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String|Array} id
   * @param {String}       state state value
   *
   * @return {void}
   */
  set(id, state) {
    id = this.getId(id);
    this.events[id] = state;
    this.callBack(id, state);
  }

  /**
   * Deletes an event
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String|Array} id
   *
   * @return {void}
   */
  unset(id) {
    id = this.getId(id);
    delete this.events[id];
    delete this.callbacks[id];
  }

  /**
   * Sets State to Loading
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String|Array} id
   *
   * @return {void}
   */
  setLoading(id) {
    this.set(
      this.getId(id),
      'loading'
    );
  }

  /**
   * Sets State to success if load value is true, or error if false
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String|Array} id
   * @param {Boolean}      value
   *
   * @return {void}
   */
  setSuccess(id, value) {
    id = this.getId(id);

    this.set(id, value ? 'success' : 'error');
  }

  /**
   * Registers an on Loaded function to be called on state change
   *
   * @public
   *
   * @param {String|Array} id       operation id
   * @param {String}       state    state value
   * @param {Function}     callable callback function
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  on(id, state, callable) {
    id = this.getId(id);
    this.setCallback(id, state, callable);
    this.callBack(id, state);
  }

  /**
   * Destroys a registered callback
   *
   * @public
   *
   * @param {String|Array} id       operation id
   * @param {String}       state    state value
   * @param {Function}     callable callback function
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  destroy(id, state, callable) {
    id = this.getId(id);

    if (!this.callbacks[id] || !this.callbacks[id][state]) {
      return;
    }

    let index = this.callbacks[id][state].indexOf(callable);

    if (index > -1) {
      this.callbacks[id][state].splice(index, 1);
    }
  }

  /**
   * Sets a loadded callback function
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {String}   id       operation id
   * @param {String}   state    calbback state
   * @param {Function} callable callback function
   *
   * @return {void}
   */
  setCallback(id, state, callback) {
    if (!this.callbacks[id]) {
      this.callbacks[id] = {};
    }

    if (!this.callbacks[id][state]) {
      this.callbacks[id][state] = [];
    }

    this.callbacks[id][state].push(callback);
  }

  /**
   * Calls all registered loaded calbacks
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {String} id    operation id
   * @param {String} state calbback state
   *
   * @return {void}
   */
  callBack(id, state) {
    if (this.events[id] !== state) {
      return;
    }

    if (!this.callbacks[id] || !this.callbacks[id][state]) {
      return;
    }


    this.callbacks[id][state].forEach((callback) => {
      callback();
    })

    delete this.callbacks[id][state];
  }

  /**
   * Transforms id to string when is passed as an Array
   * joining all values with a separator
   *
   * @public
   *
   * @param {String|Array} id
   *
   * @since 7.0.0
   *
   * @return {String}
   */
  getId(id) {
    return (Array.isArray(id)) ? id.join(';') : id;
  }
}